import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, MapPin, Phone, Mail, Calendar, Users, BookOpen,
  Heart, MessageSquare, ParkingCircle, ChevronRight,
} from "lucide-react";
import { wpImg } from "@/lib/api";

const HIGHLIGHTS = [
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/Screenshot-2024-05-19-203702.png"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/Screenshot-2024-05-19-203746.png"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/Screenshot-2024-05-19-203820-e1716166180863.png"),
];

const STATS = [
  {
    img: wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/sujood-pic_orig.jpg"),
    big: "1.8 Billion",
    label: "Muslims worldwide — nearly ¼ of the world's population (2015)",
  },
  {
    img: wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/muslim-doctor_orig.jpg"),
    big: "~5%",
    label: "of American physicians are Muslim, despite Muslims being 1–2% of the U.S. population",
  },
  {
    img: wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/indonesian-flag_orig.jpg"),
    big: "Indonesia",
    label: "is home to the largest Muslim population — 13% of all Muslims globally",
  },
];

const COMMUNITY_PHOTOS = [
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/9356183-orig.jpg"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/shaykh-abdallah-talking.jpg"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/6055163-orig.jpg"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/img-8654-orig.jpg"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/fic-diverse-dinner.jpg"),
  wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/2013-12-31-23-00-00-98-orig.jpg"),
];

const VENUE_IMG = wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/img-4356.jpg");
const PARKING_IMG = wpImg("https://flintislamiccenter.com/wp-content/uploads/2024/05/parkingimage_orig.jpg");

export default function MessageOfSubmission() {
  return (
    <div data-testid="message-of-submission-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-sand">
        <div className="absolute inset-0 bg-navy">
          <div className="absolute inset-0 pattern-girih opacity-25" />
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-gold/15 blur-3xl" />
          <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-emerald-500/5 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36 text-cream">
          <Link to="/services/outreach" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-gold/80 hover:text-gold mb-6">
            <ChevronRight size={12} className="rotate-180" /> Community Outreach
          </Link>
          <div className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold inline-flex items-center gap-2">
            <Sparkles size={12} /> An Open Invitation
          </div>
          <h1 className="display text-4xl md:text-6xl lg:text-7xl mt-6 leading-[1.05] max-w-4xl">
            Want to learn about Islam<br />
            <span className="text-gold">and meet your Muslim neighbors?</span>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-cream/85 max-w-3xl leading-relaxed font-serif italic">
            Join us for a warm, no-pressure introduction to the message of Islam — how it began, what it teaches,
            and what it means to be Muslim in our community today.
          </p>

          {/* Session Card */}
          <div className="mt-10 inline-block rounded-3xl bg-cream/10 backdrop-blur-md border border-gold/30 p-6 md:p-8 max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold flex items-center gap-2">
              <Calendar size={12} /> The Message of Submission to God
            </div>
            <div className="font-serif text-2xl md:text-3xl mt-3 leading-tight">
              Next session announced<br />after Ramadan
            </div>
            <div className="text-cream/75 text-sm mt-3">
              <strong className="text-cream">Flint Islamic Center</strong><br />
              9447 Corunna Rd, Swartz Creek, MI
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="mailto:message@gaflint.org?subject=RSVP — Message of Submission"
                data-testid="mos-rsvp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-lg"
              >
                <Mail size={14} /> RSVP by email
              </a>
              <a
                href="tel:+18107713336"
                className="inline-flex items-center gap-2 rounded-full border-2 border-cream/30 text-cream px-7 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-cream/10"
              >
                <Phone size={14} /> (810) 771-3336
              </a>
            </div>
          </div>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8 md:h-12 text-cream" preserveAspectRatio="none" viewBox="0 0 1440 60">
          <path fill="currentColor" d="M0,30 C320,60 1120,0 1440,30 L1440,60 L0,60 Z" />
        </svg>
      </section>

      {/* INTRO PARAGRAPH */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">A Living Faith</div>
          <h2 className="font-serif text-3xl md:text-5xl text-ink mt-4 leading-tight">
            One quarter of the world. <span className="text-navy italic">A thousand cultures.</span>
          </h2>
          <p className="mt-6 text-ink/70 text-lg leading-relaxed">
            Representing one quarter of the world's population, Islam encompasses a vibrant variety of cultures
            across the globe. Learn about how it all started — and why so many continue to choose Islam as their way of life.
          </p>
        </div>

        {/* HIGHLIGHT GRAPHICS */}
        <div className="max-w-6xl mx-auto mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((src, i) => (
            <div key={i} className="rounded-3xl overflow-hidden bg-white border border-sand p-3 hover:shadow-2xl hover:shadow-navy/10 hover:-translate-y-1 transition-all">
              <img src={src} alt={`Message slide ${i + 1}`} loading="lazy" className="w-full h-auto rounded-2xl" />
            </div>
          ))}
        </div>
      </section>

      {/* STATS — Who Are Muslims */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-navy text-cream relative overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-10" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Who Are Muslims?</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">A community of over a billion</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`mos-stat-${i}`} className="rounded-3xl overflow-hidden bg-cream/5 border border-cream/10 hover:border-gold/40 hover:bg-cream/10 transition-all group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <div className="font-serif text-4xl md:text-5xl text-gold leading-none">{s.big}</div>
                  <p className="text-cream/75 text-sm mt-3 leading-relaxed">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR COMMUNITY */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Our Community</div>
              <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4 leading-tight">
                From six families<br />
                <span className="text-navy italic">to a beloved family.</span>
              </h2>
              <p className="mt-6 text-ink/70 leading-relaxed">
                In the winter of <strong className="text-navy">1985</strong>, six Flint-area families came together
                realizing the need to have a place for Muslims to gather and pray. Today, we are a dynamic and
                diverse family — and you're welcome among us.
              </p>
              <p className="mt-4 text-ink/70 leading-relaxed">
                Alongside programs for spiritual development, our community regularly hosts and participates
                in <strong>outreach and interfaith activities</strong>, service projects, and charitable events.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/about" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-7 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-navy-900">
                  Our Story <ArrowRight size={14} />
                </Link>
                <Link to="/p/history-mission" className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 text-navy px-7 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-navy/5">
                  History & Mission
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-3 gap-3 md:gap-4">
                {COMMUNITY_PHOTOS.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noreferrer" className={`rounded-2xl overflow-hidden block group ${i === 0 || i === 4 ? "row-span-2 aspect-[3/5]" : "aspect-square"}`}>
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM VENUE */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-white border-y border-sand">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Program Venue</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4">An inspiring space for meaningful conversation</h2>
            <p className="mt-5 text-ink/65 max-w-3xl mx-auto leading-relaxed">
              The Flint Islamic Center is the hub of the Flint-area Muslim community, where hundreds gather each week
              for worship and learning. We share a building with <strong>Genesee Academy</strong>, our PreK-12 school
              delivering high-quality education. The program will take place in the <strong>Genesee Academy Library</strong>
              — a comfortable, welcoming space.
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden border border-sand">
            <img src={VENUE_IMG} alt="Flint Islamic Center venue" loading="lazy" className="w-full h-auto" />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-ink/70">
            <div className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> 9447 Corunna Rd, Swartz Creek, MI 48473</div>
            <div className="flex items-center gap-2"><Users size={14} className="text-gold" /> Genesee Academy Library</div>
            <div className="flex items-center gap-2"><BookOpen size={14} className="text-gold" /> Family-friendly</div>
          </div>
        </div>
      </section>

      {/* PARKING */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-cream">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold inline-flex items-center gap-2">
              <ParkingCircle size={12} /> Parking
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-ink mt-3">Easy access, ample spaces</h2>
            <p className="mt-3 text-ink/65 max-w-2xl mx-auto">Use the marked entrance at the rear of the building. Parking is free and well-lit.</p>
          </div>
          <div className="rounded-3xl overflow-hidden border border-sand bg-white p-3">
            <img src={PARKING_IMG} alt="Parking instructions" loading="lazy" className="w-full h-auto rounded-2xl" />
          </div>
        </div>
      </section>

      {/* MAP + CONTACT */}
      <section className="px-6 md:px-10 py-20 bg-white border-t border-sand">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-stretch">
          <div className="rounded-3xl overflow-hidden border border-sand shadow-xl shadow-navy/5">
            <iframe
              title="Flint Islamic Center map"
              src="https://www.google.com/maps?q=9447+Corunna+Rd,+Swartz+Creek,+MI+48473&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 380 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="rounded-3xl bg-navy text-cream p-8 md:p-10 relative overflow-hidden">
            <div className="absolute inset-0 pattern-girih opacity-15" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold flex items-center gap-2">
                <Heart size={12} /> Sign up for updates
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Got questions? <br />We'd love to chat.</h2>
              <p className="text-cream/75 mt-4 leading-relaxed">
                Whether you're curious, considering a visit, or want to bring a group — reach out anytime.
                No pressure, no commitment.
              </p>

              <div className="mt-8 space-y-4">
                <a href="tel:+18107713336" className="flex items-center gap-4 rounded-2xl bg-cream/5 border border-cream/15 hover:bg-cream/10 transition-colors px-5 py-4">
                  <Phone className="text-gold shrink-0" size={20} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Call or text</div>
                    <div className="font-serif text-xl mt-0.5">(810) 771-3336</div>
                  </div>
                </a>
                <a href="mailto:message@gaflint.org" className="flex items-center gap-4 rounded-2xl bg-cream/5 border border-cream/15 hover:bg-cream/10 transition-colors px-5 py-4">
                  <Mail className="text-gold shrink-0" size={20} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Email</div>
                    <div className="font-serif text-xl mt-0.5 break-all">message@gaflint.org</div>
                  </div>
                </a>
                <Link to="/contact" className="flex items-center gap-4 rounded-2xl bg-gold text-navy hover:bg-gold-600 transition-colors px-5 py-4">
                  <MessageSquare className="shrink-0" size={20} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">Or use our</div>
                    <div className="font-serif text-xl mt-0.5">Contact form</div>
                  </div>
                  <ArrowRight className="ml-auto" size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
